import { Fragment, type Key, type ReactNode, useMemo } from 'react';
import { Box, type Breakpoint, useMediaQuery } from '@mui/material';
import { useTheme } from '@core/contexts/ThemeContext';

export type ColumnMasonryColumns = Partial<Record<Breakpoint, number>>;

export interface ColumnMasonryProps<T> {
  items: T[];
  getKey: (item: T) => Key;
  /** `index` is the item's position in `items`, not within its column. */
  renderItem: (item: T, index: number) => ReactNode;
  /** Column count per breakpoint, like MUI Masonry. */
  columns: ColumnMasonryColumns;
  spacing?: number;
  /** Relative item height, only used to pick the shortest column. */
  estimateHeight: (item: T) => number;
}

const BREAKPOINTS_DESC: Breakpoint[] = ['xl', 'lg', 'md', 'sm', 'xs'];

/**
 * Must read the right breakpoint on the first render (`noSsr`), otherwise the
 * first paint lays out at the wrong column count.
 */
function useActiveBreakpoint(): Breakpoint {
  const { theme } = useTheme();
  const options = { noSsr: true };
  const xl = useMediaQuery(theme.breakpoints.up('xl'), options);
  const lg = useMediaQuery(theme.breakpoints.up('lg'), options);
  const md = useMediaQuery(theme.breakpoints.up('md'), options);
  const sm = useMediaQuery(theme.breakpoints.up('sm'), options);
  if (xl) return 'xl';
  if (lg) return 'lg';
  if (md) return 'md';
  if (sm) return 'sm';
  return 'xs';
}

function resolveColumnCount(
  columns: ColumnMasonryColumns,
  breakpoint: Breakpoint
): number {
  const fromIndex = BREAKPOINTS_DESC.indexOf(breakpoint);
  for (const key of BREAKPOINTS_DESC.slice(fromIndex)) {
    const count = columns[key];
    if (count != null) return Math.max(1, count);
  }
  return 1;
}

/**
 * Places each item, in order, on the column with the smallest estimated
 * height. Placement only depends on earlier items, so appending items never
 * moves the ones already rendered.
 */
function ColumnMasonry<T>({
  items,
  getKey,
  renderItem,
  columns,
  spacing = 2,
  estimateHeight,
}: ColumnMasonryProps<T>) {
  const columnCount = resolveColumnCount(columns, useActiveBreakpoint());

  const columnItems = useMemo(() => {
    const result = Array.from({ length: columnCount }, () => [] as number[]);
    const heights = new Array<number>(columnCount).fill(0);
    items.forEach((item, index) => {
      const shortest = heights.indexOf(Math.min(...heights));
      result[shortest].push(index);
      heights[shortest] += estimateHeight(item);
    });
    return result;
  }, [items, columnCount, estimateHeight]);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: spacing,
        width: '100%',
        minWidth: 0,
      }}
    >
      {columnItems.map((indexes, column) => (
        <Box
          key={column}
          sx={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: spacing,
          }}
        >
          {indexes.map((index) => (
            <Fragment key={getKey(items[index])}>
              {renderItem(items[index], index)}
            </Fragment>
          ))}
        </Box>
      ))}
    </Box>
  );
}

export default ColumnMasonry;
